/**
 * TopazSignatureProvider (CG-F, PROD-1064) — the Topaz pen-pad surface behind
 * the headless {@link SignatureCaptureProvider} interface. Captures on a
 * physical Topaz pad (SigLite T-S460 / SignatureGem) via the {@link SigWebApi}
 * bridge and normalises to the same {@link SignatureResult} the canvas provider
 * emits: PNG (always) + `.SIG` forensic biometric when the pad exposes it.
 *
 * The backend never learns the vendor — it sees `capability: 'topaz'` and a
 * PNG/biometric, identical in shape to every other provider. Legal weight
 * still comes from the server-side PAdES + Provenance, not this raster.
 *
 * Graceful by design: with no SigWeb service (non-Windows, pad unplugged, or
 * `SigWebTablet.js` not loaded) {@link isAvailable} resolves `false`, the broker
 * drops it, and the workflow falls back to the on-screen canvas. It never
 * throws from availability detection.
 *
 * @onDevice — the SigWeb call sequence (start/stop arity, `GetSigImageB64`
 * callback) is written to Topaz's documented API but is validated on hardware
 * (the pad lives on another machine). See {@link SigWebApi} notes.
 */
import type { SignatureCaptureProvider } from './provider';
import type { SignatureResult } from './types';
import { getSigWeb, isSigWebAvailable, loadSigWeb, type SigWebApi } from './sigweb';

/** Decode a base64 PNG (no data-URL header) into an `image/png` Blob. */
function base64PngToBlob(base64: string): Blob {
  // SigWeb returns raw base64; strip a data-URL prefix if a build adds one.
  const b64 = base64.includes(',') ? base64.slice(base64.indexOf(',') + 1) : base64;
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: 'image/png' });
}

export interface TopazProviderOptions {
  /** URL to a Topaz-provided `SigWebTablet.js` (vendored under `public/`, or
   *  served by the SigWeb install). When set, {@link isAvailable} lazy-loads it
   *  before probing. Omit if the script is injected elsewhere. */
  scriptUrl?: string;
  /** Seam for tests — override how the SigWeb globals are resolved. */
  getApi?: () => SigWebApi | null;
  /** Seam for tests — override the liveness probe. */
  probe?: () => boolean;
}

export class TopazSignatureProvider implements SignatureCaptureProvider {
  readonly capability = 'topaz' as const;
  readonly displayName = 'Sign on pad';

  private readonly canvas: HTMLCanvasElement;
  private readonly scriptUrl?: string;
  private readonly getApi: () => SigWebApi | null;
  private readonly probe: () => boolean;
  /** Timer handle returned by `SetTabletState(1, …)`, needed to stop capture. */
  private timer: number | null = null;

  constructor(canvas: HTMLCanvasElement, opts: TopazProviderOptions = {}) {
    this.canvas = canvas;
    this.scriptUrl = opts.scriptUrl;
    this.getApi = opts.getApi ?? (() => getSigWeb());
    this.probe = opts.probe ?? (() => isSigWebAvailable());
  }

  async isAvailable(): Promise<boolean> {
    try {
      if (!this.getApi() && this.scriptUrl) {
        await loadSigWeb(this.scriptUrl);
      }
      return this.getApi() !== null && this.probe();
    } catch {
      return false;
    }
  }

  // No start options are read here; the 0-arg form still satisfies
  // `SignatureCaptureProvider.start(opts?)` structurally.
  async start(): Promise<void> {
    const api = this.getApi();
    if (!api) throw new Error('SigWeb is not available.');
    const ctx = this.canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas 2D context unavailable for pad rendering.');

    // Match the output raster to the host canvas so the stored PNG lines up
    // with what the operator saw. Optional styling is feature-detected.
    api.SetImageXSize(this.canvas.width);
    api.SetImageYSize(this.canvas.height);
    api.SetImagePenWidth?.(2);
    api.SetJustifyMode?.(0);
    api.ClearTablet();
    // Start live capture; the 3-arg form binds the canvas ctx for live ink and
    // returns a timer handle we keep for the stop call. @onDevice arity.
    this.timer = api.SetTabletState(1, ctx, 50);
  }

  clear(): void {
    this.getApi()?.ClearTablet();
  }

  isEmpty(): boolean {
    const api = this.getApi();
    // No API or no points ⇒ treat as empty (Save-gate safe default).
    return !api || api.NumberOfTabletPoints() === 0;
  }

  async getResult(): Promise<SignatureResult> {
    const api = this.getApi();
    if (!api) throw new Error('SigWeb is not available.');
    if (api.NumberOfTabletPoints() === 0) {
      throw new Error('No signature captured.');
    }

    const png = await new Promise<Blob>((resolve, reject) => {
      try {
        api.GetSigImageB64((base64: string) => {
          if (!base64) return reject(new Error('Empty signature image.'));
          resolve(base64PngToBlob(base64));
        });
      } catch (err) {
        reject(err instanceof Error ? err : new Error(String(err)));
      }
    });

    // Forensic biometric is optional (pad/model-dependent).
    const sig = api.GetSigString?.() || undefined;

    // Stop capture once we've read the result — leaving the tablet live leaks
    // the polling timer and keeps the pad armed for the next (wrong) signer.
    this.stopCapture();

    return {
      png,
      biometric: sig ? { strokes: sig } : undefined,
      capability: this.capability,
      capturedAt: new Date().toISOString(),
    };
  }

  /** Stop live capture + release the polling timer. Safe to call repeatedly. */
  private stopCapture(): void {
    const api = this.getApi();
    if (api && this.timer != null) {
      try {
        api.SetTabletState(0, this.timer);
      } catch {
        /* best-effort teardown */
      }
    }
    this.timer = null;
  }

  /** Detach the pad on unmount — mirrors CanvasSignatureProvider.dispose(). */
  dispose(): void {
    this.stopCapture();
  }
}
